import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { visibleRarities, type Rarity } from '@anidex/shared/economy';
import type { MeDTO, PublicConfigDTO, PublicGameStatsDTO, UserDTO } from '@anidex/shared/types';
import { api } from '../api';
import { keys } from './keys';

export interface StaffSummary {
  owner: boolean;
  imageEditor: boolean;
  roleNames: string[];
}

export interface SessionPayload {
  user: UserDTO | null;
  staff: StaffSummary | null;
}

export function useSession() {
  return useQuery({
    queryKey: keys.session,
    queryFn: () => api<SessionPayload>('/auth/session'),
    staleTime: 60_000,
  });
}


export function useVisibleRarities(): Rarity[] {
  const unlocked = useSession().data?.user?.senitaUnlocked ?? false;
  return useMemo(() => visibleRarities(unlocked), [unlocked]);
}

export function useConfig() {
  return useQuery({ queryKey: keys.config, queryFn: () => api<PublicConfigDTO>('/config'), staleTime: Infinity });
}

export function usePublicGameStats() {
  return useQuery({
    queryKey: keys.publicStats,
    queryFn: () => api<PublicGameStatsDTO>('/public/stats'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}


export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api<MeDTO>('/me'),
  });
}

export function useAuthMutation<TInput>(path: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => api<{ user: UserDTO; staff: StaffSummary | null }>(path, { body: input }),
    onSuccess: (result) => {
      client.clear();
      client.setQueryData(keys.session, { user: result.user, staff: result.staff });
    },
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ ok: boolean }>('/auth/logout', { body: {} }),
    onSettled: () => {
      client.clear();
      client.setQueryData(keys.session, { user: null, staff: null });
    },
  });
}

export function useDeleteAccount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (password: string) => api<{ ok: boolean }>('/me/delete', { body: { password } }),
    onSuccess: () => {
      client.clear();
      client.setQueryData(keys.session, { user: null, staff: null });
    },
  });
}
